import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Menu, X, Home, Users, Video, Gift, Wallet,
  FileText, LogOut, Search, Bell, ChevronDown, Building2,
  Coins, TrendingUp, GitMerge, MessageSquare, Settings,
  ShieldAlert, Activity, Power, ShieldCheck, ChevronLeft, ChevronRight, UserCircle
} from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';

const Layout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const token = sessionStorage.getItem('adminToken');
  const [unreadCount, setUnreadCount] = useState(0);

  const userinfo = React.useMemo(() => {
    if (!token) return null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error(e);
      return null;
    }
  }, [token]);

  React.useEffect(() => {
    if (!token) return;
    const fetchUnreadCount = async () => {
      try {
        const response = await axios.get(`${constant.API_BASE_URL}/notifications/for-message`, {
          headers: { Authorization: token }
        });
        if (response.data.success) {
          const list = response.data.data || [];
          const unread = list.filter(n => n.notification_status === 'unread').length;
          setUnreadCount(unread);
        }
      } catch (err) {
        console.error("Error fetching unread notifications count:", err);
      }
    };
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 15000);
    return () => clearInterval(interval);
  }, [token]);

  const userType = userinfo?.user_type || 'ADMIN';
  const userName = userinfo?.user_name || 'Admin';

  const menuItems = [
    { path: '/security-logs', label: 'Security & Logs', icon: <ShieldAlert size={20} /> },
    { path: '/', label: 'Dashboard', icon: <Home size={20} /> },
    { path: '/users', label: 'User Management', icon: <Users size={20} /> },
    { path: '/sellers', label: 'Seller Management', icon: <Users size={20} /> },
    { path: '/recharge', label: 'Recharge Management', icon: <Wallet size={20} /> },
    { path: '/wallet', label: 'Wallet Management', icon: <Wallet size={20} /> },
    { path: '/coins', label: 'Coin Management', icon: <Coins size={20} /> },
    { path: '/targets-config', label: 'Target Config', icon: <TrendingUp size={20} /> },
    { path: '/store-management', label: 'Store Assets', icon: <Gift size={20} /> },
    { path: '/rewards-tracking', label: 'Rewards Tracking', icon: <Gift size={20} /> },
    { path: '/commissions-tracking', label: 'Commissions', icon: <TrendingUp size={20} /> },
    { path: '/fraud-control', label: 'Fraud & Security', icon: <ShieldCheck size={20} /> },
    { path: '/reports', label: 'Reports Section', icon: <FileText size={20} /> },
    { path: '/support', label: 'Ticket & Support', icon: <MessageSquare size={20} /> },
    { path: '/app-feedback', label: 'App Feedback', icon: <MessageSquare size={20} /> },
    { path: '/notifications', label: 'Notification System', icon: <Bell size={20} /> },
    { path: '/settings', label: 'Settings Panel', icon: <Settings size={20} /> },
    { path: '/roles', label: 'Admin Role System', icon: <ShieldAlert size={20} /> },
    { path: '/system-monitor', label: 'System Monitor', icon: <Activity size={20} /> },
    { path: '/maintenance', label: 'Maintenance Mode', icon: <Power size={20} /> },
    { path: '/bd-management', label: 'BD System', icon: <GitMerge size={20} /> },
    { path: '/agencies', label: 'Agency Management', icon: <Building2 size={20} /> },
  ].filter(item => {
    if (userType === 'BD') {
      return item.path === '/' || item.path === '/agencies';
    }
    if (userType === 'MANAGER') {
      return item.path === '/' || item.path === '/bd-management' || item.path === '/agencies';
    }
    return true;
  });

  const searchResults = menuItems.filter(m => m.label.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <div className="flex h-screen bg-[#F4F7FE] font-sans">
      {/* Sidebar - Desktop: Fixed, Mobile: Overlay */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 bg-white flex flex-col h-full
        transform transition-all duration-300 ease-in-out border-r border-gray-100
        ${isSidebarCollapsed ? 'w-[88px]' : 'w-[280px]'}
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Collapse Button */}
        <button 
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          className="absolute -right-3 top-8 bg-white border border-gray-200 text-gray-500 rounded-full p-1 shadow-sm hover:text-pink-600 hidden lg:flex z-50"
        >
          {isSidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
        {/* Brand Logo */}
        <div className="h-24 flex items-center justify-center border-b border-gray-50/50 shrink-0 px-4">
          <h1 className="flex items-center gap-2">
            <img src="/xoxo-logo.jpeg" className="w-12 h-12 rounded-full" alt="XOXO Logo" />
            {!isSidebarCollapsed && <span className="text-gray-300 text-lg font-medium whitespace-nowrap overflow-hidden transition-all duration-300">Admin</span>}
          </h1>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-2 mt-4 flex-1 overflow-y-auto scrollbar-thin">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsSidebarOpen(false)}
                className={`
                  flex items-center px-4 py-3.5 rounded-2xl transition-all duration-200 group
                  ${isSidebarCollapsed ? 'justify-center' : 'gap-4'}
                  ${isActive
                    ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg shadow-pink-500/30 lg:translate-x-2'
                    : 'text-gray-500 hover:bg-pink-50 hover:text-pink-600 lg:hover:translate-x-1'}
                `}
                title={item.label}
              >
                <span className={`${isActive ? 'text-white' : 'text-gray-400 group-hover:text-pink-500'} flex-shrink-0`}>
                  {item.icon}
                </span>
                {!isSidebarCollapsed && <span className="font-semibold tracking-wide text-sm whitespace-nowrap">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

      </aside>

      {/* Main Content Area */}
      <main className={`flex-1 flex flex-col h-screen overflow-hidden transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-[88px]' : 'lg:ml-[280px]'}`}>

        {/* Top Header */}
        <header className="h-24 bg-white/70 backdrop-blur-xl sticky top-0 z-40 flex items-center justify-between px-8 border-b border-white/20">
          <div className="flex items-center gap-4">
            <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="lg:hidden p-2 text-gray-600 bg-white rounded-lg shadow-sm">
              <Menu size={24} />
            </button>
            <div className="hidden md:flex flex-col">
              <h2 className="text-2xl font-bold text-slate-800">
                {location.pathname === '/' 
                  ? 'Dashboard' 
                  : location.pathname.startsWith('/agencies/') 
                    ? 'Agency Details'
                    : location.pathname === '/agencies' && userType === 'BD'
                      ? 'BD Management'
                      : location.pathname === '/bd-management'
                        ? 'BD Management'
                        : location.pathname.replace('/', '').replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </h2>
              <p className="text-gray-400 text-sm">Welcome back, {userName} 👋</p>
            </div>
          </div>

          {/* Right Header Controls */}
          <div className="flex items-center gap-6">
            {/* Search Bar */}
            <div className="relative hidden md:flex items-center bg-white px-4 py-3 rounded-full shadow-sm border border-gray-100 w-64 focus-within:w-80 transition-all duration-300">
              <Search size={18} className="text-gray-400" />
              <input
                type="text"
                placeholder="Search menu..."
                className="bg-transparent border-none outline-none text-sm ml-3 w-full text-gray-600 placeholder-gray-400"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSearchResults(true);
                }}
                onFocus={() => setShowSearchResults(true)}
                onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchResults.length > 0) {
                    navigate(searchResults[0].path);
                    setSearchQuery('');
                    setShowSearchResults(false);
                  }
                }}
              />
              {showSearchResults && searchQuery && (
                <div className="absolute top-full left-0 mt-2 w-full bg-white border border-gray-100 rounded-xl shadow-xl overflow-hidden z-50 max-h-60 overflow-y-auto">
                  {searchResults.length > 0 ? (
                    searchResults.map((result, idx) => (
                      <div 
                        key={idx}
                        className="px-4 py-3 hover:bg-pink-50 cursor-pointer flex items-center gap-3 text-sm text-gray-700 hover:text-pink-600 transition"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          navigate(result.path);
                          setSearchQuery('');
                          setShowSearchResults(false);
                        }}
                      >
                        {result.icon}
                        <span className="font-semibold">{result.label}</span>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-sm text-gray-400 text-center">No matching section found.</div>
                  )}
                </div>
              )}
            </div>

            {/* Notification Bell */}
            <button 
              onClick={() => navigate('/notifications')}
              className="relative p-3 bg-white rounded-full shadow-sm hover:shadow-md transition text-gray-500 cursor-pointer"
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center border-2 border-white animate-pulse">
                  {unreadCount}
                </span>
              )}
            </button>

            {/* Profile Dropdown */}
            <div className="flex items-center gap-3 pl-6 border-l border-gray-200">
              <div className="text-right hidden md:block">
                <p className="text-sm font-bold text-slate-700">{userName}</p>
                <button 
                  onClick={() => {
                    sessionStorage.removeItem('adminToken');
                    navigate('/login');
                  }}
                  className="text-xs text-red-500 hover:text-red-600 font-medium flex items-center gap-1 mt-0.5"
                >
                  <LogOut size={12} /> Logout
                </button>
              </div>
              <div 
                className="h-12 w-12 rounded-full bg-gradient-to-tr from-pink-500 to-purple-600 p-[2px] cursor-pointer hover:shadow-lg transition" 
                onMouseDown={() => navigate('/settings')}
                title="Go to Profile"
              >
                <div className="h-full w-full rounded-full bg-slate-100 flex items-center justify-center overflow-hidden">
                  <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${userName}`} alt="Profile" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto bg-[#F4F7FE] p-4 md:p-8">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </div>
      </main>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden" onClick={() => setIsSidebarOpen(false)} />
      )}
    </div>
  );
};

export default Layout;